import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faChevronRight, faChevronDown, faLightbulb,
  faUsers, faUserCheck, faUserPlus, faUserEdit, faUserSlash,
  faCalendarCheck, faClock, faFileAlt, faChartBar,
  faBriefcase, faBuilding, faStar, faComment,
  faCheckCircle, faTimesCircle, faEye, faEdit, faTrash,
  faEnvelope, faPhone, faMapMarkerAlt, faIdCard,
  faMoneyBillWave, faUniversity, faCalendarAlt,
  faSearch, faFilter, faPlus, faSave, faPrint,
  faUser,
  faUserShield
} from '@fortawesome/free-solid-svg-icons';

const AdminTutoRH = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const sections = [
    {
      id: 'employes',
      icon: faUsers,
      title: 'I. Gestion des Employés',
      color: '#3b82f6',
      intro: 'Ce tutoriel vous explique pas à pas comment ajouter et modifier les informations d\'un employé dans l\'application. La suppression définitive n\'est pas disponible : nous utilisons la désactivation pour conserver l\'historique et l\'intégrité des données (paies, contrats, etc.).',
      steps: [
        {
          step: '1',
          title: 'Accéder à la page "Gestion des employés"',
          description: 'Connectez-vous à l\'application et suivez le chemin suivant :',
          details: [
            '📋 Dans le menu latéral (gauche), repérez la rubrique "Employé"',
            '🔽 Cliquez pour dérouler les sous-menus',
            '📂 Sélectionnez "Gestion des employés"',
            '📊 Vous arrivez sur la page principale avec la liste complète des employés (matricule, nom, prénom, fonction, statut)'
          ],
          tip: '💡 Astuce : La liste est triée par ordre alphabétique. Utilisez la barre de recherche pour trouver rapidement un employé.'
        },
        {
          step: '2',
          title: 'Ajouter un nouvel employé',
          description: 'Pour saisir un nouvel employé dans la base de données :',
          details: [
            '🔵 Cliquez sur "Ajouter un employé" (en haut à droite)',
            '📝 Une fenêtre modale s\'ouvre avec le formulaire de saisie',
            '📋 Remplissez les champs organisés par sections :',
            '   👤 Identité (Matricule, Nom, Postnom, Prénom, Date de naissance, Sexe)',
            '   📞 Contact (Téléphone, Email)',
            '   📍 Adresse (Province, Ville, Commune, Quartier)',
            '   💼 Profession (Service, Département, Fonction, Type de contrat, Salaire)',
            '   🏦 Banque & Sécurité sociale (Compte bancaire, N° CNSS, N° INPP, N° NIF)',
            '   🆘 Urgence (Personne à contacter en cas d\'urgence)',
            '💾 Cliquez sur "Enregistrer" en bas de la fenêtre'
          ],
          tip: '💡 Astuce : Les champs marqués d\'un astérisque (*) sont obligatoires. Le matricule doit être unique.'
        },
        {
          step: '3',
          title: 'Modifier un employé existant',
          description: 'Pour corriger ou mettre à jour les informations d\'un employé :',
          details: [
            '🔍 Recherchez l\'employé concerné dans le tableau',
            '✏️ Cliquez sur "Modifier" (icône crayon) dans la colonne Actions',
            '📝 La fenêtre modale s\'ouvre avec les champs pré-remplis',
            '✏️ Modifiez les informations souhaitées',
            '💾 Cliquez sur "Enregistrer" pour valider'
          ],
          tip: '💡 Astuce : Les modifications sont immédiatement sauvegardées et le tableau se met à jour automatiquement.'
        },
        {
          step: '4',
          title: 'Désactiver / Réactiver un employé',
          description: 'La suppression est remplacée par un système de désactivation pour préserver l\'historique :',
          details: [
            '🔴 Désactiver : Cliquez sur "Désactiver" (rouge) dans la colonne Actions',
            '📊 L\'employé devient "Inactif" et n\'est plus proposé dans les listes déroulantes',
            '🟢 Réactiver : Cliquez sur "Réactiver" (vert) pour remettre l\'employé en statut "Actif"'
          ],
          warning: '⚠️ Important : La désactivation préserve l\'intégrité des contrats, bulletins de salaire et documents associés.'
        }
      ]
    },
    {
      id: 'presence',
      icon: faClock,
      title: 'II. Contrôle de Présence',
      color: '#10b981',
      intro: 'Ce tutoriel vous explique comment saisir les présences quotidiennes des employés via un système de cartes avec trois statuts : Présent, Absent ou Retard.',
      steps: [
        {
          step: '1',
          title: 'Accéder à la page "Contrôle de présence"',
          description: 'Dans le menu latéral, repérez la rubrique "Présence" ou "Pointage" :',
          details: [
            '📋 Cliquez sur le sous-menu "Contrôle de présence"',
            '👥 Vous arrivez sur une page affichant tous les employés actifs sous forme de cartes',
            '🖼️ Chaque carte présente : photo/avatar, nom complet, matricule'
          ],
          tip: '💡 Astuce : Les cartes facilitent la reconnaissance visuelle des employés.'
        },
        {
          step: '2',
          title: 'Saisir le statut de chaque employé',
          description: 'Pour chaque carte, sélectionnez un statut :',
          details: [
            '🟢 P = Présent(e) - L\'employé était présent à l\'heure prévue',
            '🔴 A = Absent(e) - L\'employé était absent toute la journée',
            '🟡 R = Retard - L\'employé est arrivé en retard'
          ],
          warning: '⚠️ Important : Vous devez choisir un seul statut par employé. Ne laissez pas de carte sans sélection.'
        },
        {
          step: '3',
          title: 'Choisir la date du pointage',
          description: 'En haut de la page, un champ "Date" permet de sélectionner le jour du pointage :',
          details: [
            '📅 Par défaut, la date du jour est pré-remplie',
            '📆 Modifiez-la si vous saisissez des présences pour un autre jour (rattrapage)'
          ],
          tip: '💡 Astuce : Vous pouvez pointer pour une date passée ou future selon vos besoins.'
        },
        {
          step: '4',
          title: 'Enregistrer les présences',
          description: 'Une fois tous les statuts sélectionnés :',
          details: [
            '💾 Cliquez sur "Enregistrer" (en bas de page ou en haut à droite)',
            '✅ Un message de confirmation s\'affiche : "Présences enregistrées avec succès"',
            '🔄 La page se réinitialise pour une nouvelle saisie'
          ],
          tip: '💡 Astuce : Seul le dernier statut enregistré pour une date et un employé est conservé.'
        },
        {
          step: '5',
          title: 'Modifier une saisie déjà enregistrée',
          description: 'Si vous devez corriger une erreur :',
          details: [
            '📅 Revenez à la page "Contrôle de présence"',
            '📆 Sélectionnez la date concernée',
            '✏️ Les cartes afficheront le statut précédemment enregistré',
            '✏️ Modifiez le statut pour les employés concernés',
            '💾 Cliquez de nouveau sur "Enregistrer"'
          ],
          tip: '💡 Astuce : Toutes les modifications sont horodatées et tracées (userId).'
        }
      ]
    },
    {
      id: 'evaluations',
      icon: faStar,
      title: 'III. Évaluation des Agents',
      color: '#8b5cf6',
      intro: 'Ce tutoriel vous explique comment réaliser l\'évaluation d\'un agent à l\'aide d\'un formulaire modal permettant de noter chaque critère sur 5 étoiles.',
      steps: [
        {
          step: '1',
          title: 'Accéder à la page "Évaluations"',
          description: 'Dans le menu latéral, repérez la rubrique "RH" ou "Évaluations" :',
          details: [
            '📋 Cliquez sur le sous-menu "Évaluation des agents"',
            '📊 Vous arrivez sur une page affichant la liste des évaluations existantes',
            '📋 Colonnes : Agent, Évaluateur, Date, Note globale, Actions'
          ],
          tip: '💡 Astuce : Les évaluations sont triées par date décroissante.'
        },
        {
          step: '2',
          title: 'Démarrer une nouvelle évaluation',
          description: 'Pour évaluer un agent :',
          details: [
            '🔵 Cliquez sur "Nouvelle évaluation" (en haut à droite)',
            '📝 Une fenêtre modale s\'ouvre'
          ],
          tip: '💡 Astuce : Vous pouvez évaluer un agent à la fois.'
        },
        {
          step: '3',
          title: 'Sélectionner l\'agent à évaluer',
          description: 'Dans la modale, choisissez l\'employé concerné :',
          details: [
            '🔍 Utilisez le champ de recherche pour trouver l\'agent par nom ou matricule',
            '👤 Sélectionnez-le dans la liste déroulante',
            '✅ Ses informations s\'affichent pour confirmation'
          ],
          tip: '💡 Astuce : Vérifiez bien l\'identité de l\'agent avant de commencer l\'évaluation.'
        },
        {
          step: '4',
          title: 'Noter chaque critère avec des étoiles',
          description: 'La modale affiche les critères d\'évaluation prédéfinis avec un système de 5 étoiles :',
          details: [
            '⭐ 1 = Très insuffisant',
            '⭐⭐ 2 = Insuffisant',
            '⭐⭐⭐ 3 = Satisfaisant',
            '⭐⭐⭐⭐ 4 = Bien',
            '⭐⭐⭐⭐⭐ 5 = Excellent'
          ],
          tip: '💡 Astuce : Passez votre souris sur les étoiles pour voir la note s\'afficher.'
        },
        {
          step: '5',
          title: 'Ajouter un commentaire et enregistrer',
          description: 'Pour finaliser l\'évaluation :',
          details: [
            '📝 Saisissez un commentaire général (facultatif)',
            '📊 Vérifiez la note globale calculée automatiquement',
            '💾 Cliquez sur "Enregistrer"',
            '✅ Un message de confirmation s\'affiche'
          ],
          tip: '💡 Astuce : Le commentaire est utile pour l\'agent et pour l\'historique.'
        },
        {
          step: '6',
          title: 'Consulter l\'historique des évaluations',
          description: 'Pour consulter les évaluations passées :',
          details: [
            '👁️ Cliquez sur "Voir" dans la colonne Actions',
            '📋 Une vue détaillée affiche tous les critères avec leurs notes',
            '📝 Le commentaire est également visible'
          ],
          tip: '💡 Astuce : Cette vue est utile pour les entretiens annuels et les décisions de promotion.'
        }
      ]
    },
    {
      id: 'conges',
      icon: faCalendarCheck,
      title: 'IV. Gestion des Congés',
      color: '#f59e0b',
      intro: 'Ce tutoriel explique le processus complet de gestion des congés : de la demande (depuis le profil employé) jusqu\'à la validation ou au refus par le responsable.',
      steps: [
        {
          step: '1',
          title: 'Faire une demande de congé',
          description: 'Pour demander un congé, un employé ou son manager doit :',
          details: [
            '👤 Accéder au profil de l\'employé via "Employés" > "Gestion des employés"',
            '📋 Rechercher l\'employé et cliquer sur "Voir le profil"',
            '📝 Dans l\'onglet "Congés", cliquer sur "Nouvelle demande de congé"',
            '📋 Remplir le formulaire : Type de congé, Dates, Motif',
            '📤 Cliquer sur "Envoyer la demande"'
          ],
          tip: '💡 Astuce : Assurez-vous que les dates sont cohérentes (date de fin postérieure à la date de début).'
        },
        {
          step: '2',
          title: 'Valider ou refuser une demande (RH / Manager)',
          description: 'Pour traiter les demandes en attente :',
          details: [
            '📋 Accéder à la section "Congés" > "Gestion des congés"',
            '📊 Consulter les demandes avec le statut "En attente"',
            '🟢 Cliquer sur "Valider" pour approuver le congé',
            '🔴 Cliquer sur "Refuser" pour rejeter la demande'
          ],
          tip: '💡 Astuce : Vérifiez le solde de congés de l\'employé avant de valider une demande.'
        },
        {
          step: '3',
          title: 'Consulter l\'historique des congés',
          description: 'Pour suivre les absences :',
          details: [
            '👤 Dans le profil de l\'employé, onglet "Historique des congés"',
            '📋 Toutes les demandes passées (approuvées, refusées, en attente) sont visibles',
            '🔍 Utilisez les filtres pour rechercher par période ou par employé'
          ],
          tip: '💡 Astuce : L\'historique permet de préparer les plannings et d\'anticiper les absences.'
        }
      ]
    },
    {
      id: 'missions',
      icon: faBriefcase,
      title: 'V. Gestion des Missions et Rapports',
      color: '#ec4899',
      intro: 'Ce tutoriel vous explique comment créer des missions dans des entreprises, puis rédiger un rapport associé à chaque mission pour suivre les interventions (inspections, audits, formations, etc.).',
      steps: [
        {
          step: '1',
          title: 'Créer une mission',
          description: 'Pour créer une mission dans une entreprise :',
          details: [
            '📋 Accéder à la page "Missions" > "Liste des missions"',
            '🔵 Cliquer sur "Nouvelle mission"',
            '📝 Remplir le formulaire :',
            '   🏢 Entreprise (sélectionner dans la liste)',
            '   📝 Description de la mission',
            '   📅 Date de la mission',
            '💾 Cliquer sur "Enregistrer"'
          ],
          tip: '💡 Astuce : Si l\'entreprise n\'existe pas, créez-la d\'abord via "Gestion des entreprises".'
        },
        {
          step: '2',
          title: 'Rédiger un rapport',
          description: 'Pour associer un rapport à une mission :',
          details: [
            '📋 Dans la liste des missions, repérez la mission concernée',
            '📝 Cliquer sur "Rédiger un rapport" (colonne Actions)',
            '📋 Remplir le formulaire :',
            '   🎯 Cible (Agent nettoyage, Chauffeur, Gardiennage, Autres)',
            '   📋 Type de mission (Inspection, Audit, Formation, Supervision, etc.)',
            '   📝 Commentaire (observations, points forts, axes d\'amélioration)',
            '   ⭐ Note (sur 5 ou 10 selon votre barème)',
            '   📊 Statut (Brouillon, En cours, Terminé, Validée)',
            '   📅 Date du rapport',
            '💾 Cliquer sur "Enregistrer"'
          ],
          tip: '💡 Astuce : Le commentaire est important pour tracer les actions réalisées et les recommandations.'
        },
        {
          step: '3',
          title: 'Consulter et modifier un rapport',
          description: 'Pour gérer les rapports existants :',
          details: [
            '👁️ Cliquer sur "Voir" pour consulter un rapport',
            '✏️ Cliquer sur "Modifier" pour mettre à jour un rapport',
            '📋 Tous les champs sont modifiables',
            '💾 Enregistrer les modifications'
          ],
          tip: '💡 Astuce : Utilisez les statuts pour piloter l\'avancement des missions (Brouillon → Terminé → Validée).'
        }
      ]
    }
  ];

  return (
    <>
      <style>{`
        /* ============================================================
           STYLES – ADMIN TUTO RESSOURCES HUMAINES
           ============================================================ */
        :root {
          --bg-gradient: radial-gradient(circle at 10% 20%, #0f172a, #020617);
          --card-bg: rgba(255,255,255,0.04);
          --card-border: rgba(255,255,255,0.06);
          --text-primary: #e2e8f0;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --accent-blue: #3b82f6;
          --accent-green: #10b981;
          --accent-amber: #f59e0b;
          --accent-purple: #8b5cf6;
          --accent-red: #ef4444;
          --accent-cyan: #06b6d4;
          --accent-pink: #ec4899;
          --radius: 20px;
          --shadow: 0 20px 60px rgba(0,0,0,0.6);
        }

        .tuto-container {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 2rem 1.5rem;
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.6;
        }

        .tuto-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }

        .tuto-header .back-link {
          color: var(--text-secondary);
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 30px;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          transition: 0.2s;
          font-size: 0.9rem;
        }
        .tuto-header .back-link:hover {
          background: rgba(255,255,255,0.08);
          color: #fff;
        }

        .tuto-header h1 {
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 700;
          background: linear-gradient(135deg, #60a5fa, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0;
        }

        .tuto-header .subtitle {
          color: var(--text-secondary);
          font-size: clamp(0.9rem, 1.2vw, 1.1rem);
          margin-top: 0.2rem;
        }

        .tuto-intro {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: var(--radius);
          padding: 1.5rem 2rem;
          margin-bottom: 2rem;
        }
        .tuto-intro .badge-roles {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }
        .tuto-intro .badge-roles span {
          padding: 0.25rem 0.8rem;
          border-radius: 30px;
          font-size: 0.75rem;
          font-weight: 500;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          color: var(--text-secondary);
        }
        .tuto-intro .badge-roles .admin { background: rgba(244,63,94,0.15); color: #f43f5e; border-color: rgba(244,63,94,0.15); }
        .tuto-intro .badge-roles .dsi { background: rgba(6,182,212,0.15); color: #06b6d4; border-color: rgba(6,182,212,0.15); }
        .tuto-intro .badge-roles .rh { background: rgba(16,185,129,0.15); color: #10b981; border-color: rgba(16,185,129,0.15); }

        .section-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.04);
          border-radius: var(--radius);
          margin-bottom: 1.2rem;
          overflow: hidden;
          transition: 0.3s;
        }
        .section-card:hover {
          border-color: rgba(255,255,255,0.08);
        }

        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1.2rem 1.8rem;
          cursor: pointer;
          transition: 0.2s;
          background: rgba(255,255,255,0.01);
          user-select: none;
        }
        .section-header:hover {
          background: rgba(255,255,255,0.03);
        }
        .section-header .left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .section-header .left .icon {
          font-size: 1.5rem;
          width: 36px;
          text-align: center;
        }
        .section-header .left .title {
          font-size: clamp(1.1rem, 1.4vw, 1.3rem);
          font-weight: 600;
        }
        .section-header .left .badge {
          font-size: 0.65rem;
          padding: 0.15rem 0.6rem;
          border-radius: 30px;
          background: rgba(255,255,255,0.04);
          color: var(--text-muted);
        }
        .section-header .toggle-icon {
          color: var(--text-muted);
          transition: transform 0.3s;
          font-size: 0.9rem;
        }
        .section-header .toggle-icon.open {
          transform: rotate(180deg);
        }

        .section-content {
          padding: 0 1.8rem 1.8rem 1.8rem;
          border-top: 1px solid rgba(255,255,255,0.03);
          animation: slideDown 0.3s ease;
        }

        .section-intro {
          padding: 0.8rem 0 1rem 0;
          color: var(--text-secondary);
          font-style: italic;
          border-bottom: 1px solid rgba(255,255,255,0.03);
          margin-bottom: 1rem;
          font-size: 0.95rem;
        }
        .section-intro strong {
          color: var(--text-primary);
        }

        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .step-item {
          display: flex;
          gap: 1rem;
          padding: 0.8rem 0;
          border-bottom: 1px solid rgba(255,255,255,0.02);
        }
        .step-item:last-child {
          border-bottom: none;
        }
        .step-item .step-number {
          font-weight: 700;
          font-size: 1.1rem;
          min-width: 28px;
          color: var(--text-muted);
        }
        .step-item .step-content {
          flex: 1;
        }
        .step-item .step-content .step-title {
          font-weight: 600;
          color: #fff;
          margin-bottom: 0.2rem;
        }
        .step-item .step-content .step-desc {
          color: var(--text-secondary);
          font-size: 0.95rem;
        }
        .step-item .step-content .step-details {
          margin-top: 0.4rem;
          padding-left: 1.2rem;
        }
        .step-item .step-content .step-details li {
          color: var(--text-secondary);
          font-size: 0.9rem;
          list-style: none;
          padding: 0.1rem 0;
        }
        .step-item .step-content .step-details li::before {
          content: '• ';
          color: var(--accent-blue);
        }
        .step-item .step-content .step-tip {
          margin-top: 0.4rem;
          padding: 0.4rem 1rem;
          background: rgba(59,130,246,0.08);
          border-left: 3px solid var(--accent-blue);
          border-radius: 6px;
          font-size: 0.9rem;
          color: var(--text-secondary);
        }
        .step-item .step-content .step-warning {
          margin-top: 0.4rem;
          padding: 0.4rem 1rem;
          background: rgba(239,68,68,0.08);
          border-left: 3px solid var(--accent-red);
          border-radius: 6px;
          font-size: 0.9rem;
          color: #fca5a5;
        }

        .footer-tuto {
          text-align: center;
          padding-top: 2rem;
          border-top: 1px solid rgba(255,255,255,0.04);
          color: var(--text-secondary);
          font-size: 0.85rem;
        }

        @media (max-width: 768px) {
          .tuto-container { padding: 1.2rem; }
          .tuto-header { flex-direction: column; align-items: flex-start; }
          .section-header { padding: 1rem 1.2rem; flex-wrap: wrap; gap: 0.5rem; }
          .section-content { padding: 0 1.2rem 1.2rem 1.2rem; }
          .step-item { flex-direction: column; gap: 0.3rem; }
          .tuto-intro { padding: 1rem; }
        }
      `}</style>

      <div className="tuto-container">
        {/* ===== EN-TÊTE ===== */}
        <div className="tuto-header">
          <a href="/dashboard" className="back-link">
            <FontAwesomeIcon icon={faArrowLeft} /> Retour
          </a>
          <div>
            <h1>👥 Tutoriel Ressources Humaines</h1>
            <div className="subtitle">Bisenter ERP – Guide complet pour la gestion des employés, présences, évaluations, congés et missions</div>
          </div>
        </div>

        {/* ===== INTRODUCTION ===== */}
        <div className="tuto-intro">
          <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            Ce tutoriel couvre l'ensemble des fonctionnalités du module <strong style={{ color: '#fff' }}>Ressources Humaines (RH)</strong> 
            de l'application Bisenter ERP. Il est destiné aux <strong style={{ color: '#fff' }}>Super Administrateurs</strong>, 
            aux <strong style={{ color: '#fff' }}>DSI</strong> et aux responsables <strong style={{ color: '#fff' }}>RH</strong>.
          </p>
          <div className="badge-roles">
            <span className="admin"><FontAwesomeIcon icon={faUserShield} /> Super Administrateur</span>
            <span className="dsi"><FontAwesomeIcon icon={faUser} /> DSI</span>
            <span className="rh"><FontAwesomeIcon icon={faUsers} /> Ressources Humaines</span>
          </div>
        </div>

        {/* ===== SECTIONS ===== */}
        {sections.map((section) => (
          <div key={section.id} className="section-card">
            <div className="section-header" onClick={() => toggleSection(section.id)}>
              <div className="left">
                <span className="icon" style={{ color: section.color }}>
                  <FontAwesomeIcon icon={section.icon} />
                </span>
                <span className="title">{section.title}</span>
                <span className="badge">{section.steps.length} étapes</span>
              </div>
              <FontAwesomeIcon
                icon={expandedSection === section.id ? faChevronDown : faChevronRight}
                className={`toggle-icon ${expandedSection === section.id ? 'open' : ''}`}
              />
            </div>

            {expandedSection === section.id && (
              <div className="section-content">
                <div className="section-intro">
                  <strong>📖 À lire :</strong> {section.intro}
                </div>
                {section.steps.map((step, index) => (
                  <div key={index} className="step-item">
                    <div className="step-number">{step.step}.</div>
                    <div className="step-content">
                      <div className="step-title">{step.title}</div>
                      <div className="step-desc">{step.description}</div>
                      {step.details && (
                        <ul className="step-details">
                          {step.details.map((detail, idx) => (
                            <li key={idx}>{detail}</li>
                          ))}
                        </ul>
                      )}
                      {step.tip && (
                        <div className="step-tip">
                          <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '0.5rem', color: 'var(--accent-blue)' }} />
                          {step.tip}
                        </div>
                      )}
                      {step.warning && (
                        <div className="step-warning">
                          <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '0.5rem', color: 'var(--accent-red)' }} />
                          {step.warning}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* ===== RÉSUMÉ DES RÔLES ET ACTIONS ===== */}
        <div className="glass-card" style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(59,130,246,0.15)',
          borderRadius: 'var(--radius)',
          padding: '1.5rem',
          marginTop: '1.5rem'
        }}>
          <h3 style={{ color: '#fff', marginBottom: '0.8rem' }}>
            <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'var(--accent-green)', marginRight: '0.5rem' }} />
            Récapitulatif des actions par rôle
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div style={{ background: 'rgba(255,255,255,0.02)', padding: '0.8rem', borderRadius: '12px' }}>
              <h4 style={{ color: '#f43f5e', marginBottom: '0.3rem' }}>Super Admin</h4>
              <ul style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', paddingLeft: '1.2rem' }}>
                <li>Configuration générale</li>
                <li>Gestion des administrateurs</li>
                <li>Accès à toutes les sections</li>
              </ul>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.02)', padding: '0.8rem', borderRadius: '12px' }}>
              <h4 style={{ color: '#06b6d4', marginBottom: '0.3rem' }}>DSI</h4>
              <ul style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', paddingLeft: '1.2rem' }}>
                <li>Paramétrage technique</li>
                <li>Maintenance des données</li>
                <li>Gestion des utilisateurs</li>
              </ul>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.02)', padding: '0.8rem', borderRadius: '12px' }}>
              <h4 style={{ color: '#10b981', marginBottom: '0.3rem' }}>RH</h4>
              <ul style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', paddingLeft: '1.2rem' }}>
                <li>Gestion des employés</li>
                <li>Contrôle de présence</li>
                <li>Évaluations</li>
                <li>Gestion des congés</li>
                <li>Missions et rapports</li>
              </ul>
            </div>
          </div>
        </div>

        {/* ===== PIED DE PAGE ===== */}
        <div className="footer-tuto">
          <p>
            <strong>Besoin d'aide ?</strong> Contactez le support technique à <span style={{ color: 'var(--accent-blue)' }}>support@bisenter.com</span>
          </p>
          <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            © {new Date().getFullYear()} Bisenter – Tous droits réservés.
          </p>
        </div>
      </div>
    </>
  );
};

// Ajout de l'icône ExclamationTriangle manquante
const faExclamationTriangle = {
  prefix: 'fas',
  iconName: 'exclamation-triangle',
  icon: [576, 512, [], 'f071', 'M569.5 440.4L320.2 40.6c-14.3-25.3-50.1-25.3-64.4 0L6.5 440.4c-14.3 25.3 3.6 57.1 32.2 57.1h498.6c28.5 0 46.5-31.8 32.2-57.1zM288 352c-17.7 0-32-14.3-32-32s14.3-32 32-32 32 14.3 32 32-14.3 32-32 32zm0-112c-17.7 0-32-14.3-32-32V160c0-17.7 14.3-32 32-32s32 14.3 32 32v48c0 17.7-14.3 32-32 32z']
};

export default AdminTutoRH;